import React, { useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Button,
  Text,
  Icon,
  Badge,
  Select,
  InlineStack,
  BlockStack,
  Box,
  Divider,
  InlineGrid,
  SkeletonBodyText,
  Toast
} from '@shopify/polaris';
import {
  EmailMajor,
  LocationMajor,
  PackageMajor,
  ChecklistMajor,
  LanguageMinor,
  MobileMajor
} from '@shopify/polaris-icons';
import { useTranslation } from 'react-i18next';
import api from '../utils/axiosConfig';

const GetStarted = () => {
  const { t } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [sendingTest, setSendingTest] = useState(false);
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastError, setToastError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [storeInfo, setStoreInfo] = useState(null);

  // Lista de idiomas disponíveis (22 idiomas)
  const availableLanguages = [
    { label: 'English 🇬🇧', value: 'en' },
    { label: 'German 🇩🇪', value: 'de' },
    { label: 'Spanish 🇪🇸', value: 'es' },
    { label: 'French 🇫🇷', value: 'fr' },
    { label: 'Italian 🇮🇹', value: 'it' },
    { label: 'Portuguese 🇵🇹🇧🇷', value: 'pt' },
    { label: 'Russian 🇷🇺', value: 'ru' },
    { label: 'Dutch 🇳🇱', value: 'nl' },
    { label: 'Greek 🇬🇷', value: 'el' },
    { label: 'Polish 🇵🇱', value: 'pl' },
    { label: 'Hungarian 🇭🇺', value: 'hu' },
    { label: 'Swedish 🇸🇪', value: 'sv' },
    { label: 'Danish 🇩🇰', value: 'da' },
    { label: 'Norwegian 🇳🇴', value: 'no' },
    { label: 'Czech 🇨🇿', value: 'cs' },
    { label: 'Romanian 🇷🇴', value: 'ro' },
    { label: 'Bulgarian 🇧🇬', value: 'bg' },
    { label: 'Croatian 🇭🇷', value: 'hr' },
    { label: 'Serbian 🇷🇸', value: 'sr' },
    { label: 'Slovak 🇸🇰', value: 'sk' },
    { label: 'Slovenian 🇸🇮', value: 'sl' },
    { label: 'Estonian 🇪🇪', value: 'et' }
  ];

  useEffect(() => {
    loadStoreInfo();
  }, []);

  const loadStoreInfo = async () => {
    try {
      setLoading(true);
      const response = await api.get('/emails/store-info');
      if (response.data.success) {
        setStoreInfo(response.data.store);
      }
    } catch (error) {
      console.error('Error loading store info:', error);
      showToast('Failed to load store information. Please refresh the page.', true);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, isError = false) => {
    setToastMessage(message);
    setToastError(isError);
    setToastActive(true);
  };

  const sendTestEmail = async () => {
    try {
      setSendingTest(true);
      
      const response = await api.post('/emails/test', {
        language: selectedLanguage,
        emailNumber: 1 // Enviando o primeiro email como teste
      });

      if (response.data.success) {
        const recipientEmail = response.data.recipientEmail || storeInfo?.email || 'your email';
        showToast(`Test email sent successfully to ${recipientEmail}! Check your inbox in a few moments.`);
      } else {
        throw new Error(response.data.error || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      showToast(error.response?.data?.error || 'Failed to send test email. Please try again.', true);
    } finally {
      setSendingTest(false);
    }
  };

  const features = [
    {
      icon: EmailMajor,
      title: t('getStarted.features.automatedEmails'),
      description: t('getStarted.features.automatedEmailsDesc'),
      color: 'base'
    },
    {
      icon: LanguageMinor,
      title: t('getStarted.features.multiLanguage'),
      description: t('getStarted.features.multiLanguageDesc'),
      color: 'base'
    },
    {
      icon: LocationMajor,
      title: t('getStarted.features.realTimeTracking'),
      description: t('getStarted.features.realTimeTrackingDesc'),
      color: 'base'
    },
    {
      icon: MobileMajor,
      title: t('getStarted.features.mobileOptimized'),
      description: t('getStarted.features.mobileOptimizedDesc'),
      color: 'base'
    }
  ];


  if (loading) {
    return (
      <Page title={t('getStarted.title')}>
        <Layout>
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={5} />
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const toastMarkup = toastActive ? (
    <Toast 
      content={toastMessage} 
      onDismiss={() => setToastActive(false)}
      error={toastError}
    />
  ) : null;

  return (
    <Page
      title={t('getStarted.title')}
      subtitle={t('getStarted.subtitle')}
    >
      {toastMarkup}
      
      <Layout>
        {/* Welcome Section - Custom styled to avoid i18n conflict */}
        <Layout.Section>
          <div style={{
            backgroundColor: '#f0f9ff',
            border: '1px solid #3b82f6',
            borderRadius: '8px',
            padding: '20px',
            display: 'flex',
            alignItems: 'start',
            gap: '16px'
          }}>
            <div style={{
              backgroundColor: '#3b82f6',
              borderRadius: '6px',
              padding: '8px',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Icon source={PackageMajor} color="base" />
            </div>
            <div>
              <h3 style={{ color: '#1e40af', margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600' }}>
                {t('getStarted.welcomeTitle')}
              </h3>
              <p style={{ color: '#1e40af', margin: '0', lineHeight: '1.5' }}>
                {t('getStarted.welcomeDescription')}
              </p>
            </div>
          </div>
        </Layout.Section>

        {/* How It Works - Modern Grid Layout */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <Text variant="headingMd" as="h2">
                {t('getStarted.howItWorks')}
              </Text>
              <Text>
                {t('getStarted.howItWorksDescription')}
              </Text>
            </Box>
            <Divider />
            
            <Box padding="400">
              <InlineGrid columns={{ xs: '1', sm: '2' }} gap="400">
                {features.map((feature, index) => (
                  <Card key={index} padding="400">
                    <InlineStack gap="300" align="start">
                      <div style={{ 
                        backgroundColor: index === 0 ? '#e3f1df' : index === 1 ? '#e0f5ff' : index === 2 ? '#fff7ed' : '#f0f9ff', 
                        padding: '12px', 
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Icon source={feature.icon} color={index === 0 ? 'success' : index === 1 ? 'highlight' : index === 2 ? 'warning' : 'primary'} />
                      </div>
                      <BlockStack gap="200">
                        <Text variant="headingSm" as="h3">{feature.title}</Text>
                        <Text color="subdued">{feature.description}</Text>
                      </BlockStack>
                    </InlineStack>
                  </Card>
                ))}
              </InlineGrid>
            </Box>
          </Card>
        </Layout.Section>

        {/* Quick Setup Guide - Interactive Steps */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <Text variant="headingMd" as="h2">
                {t('getStarted.quickSetupGuide') || 'Quick Setup Guide'}
              </Text>
              <Text color="subdued">
                {t('getStarted.quickSetupGuideDesc') || 'Complete these 3 simple steps to start tracking orders and delighting customers'}
              </Text>
            </Box>
            <Divider />
            
            <Box padding="400">
              <BlockStack gap="400">
                {/* Step 1: Add Tracking Page */}
                <Card padding="400">
                  <InlineStack gap="300" align="start">
                    <div style={{ 
                      backgroundColor: '#e3f1df', 
                      padding: '12px', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '40px',
                      minHeight: '40px'
                    }}>
                      <Text variant="headingSm" as="span" fontWeight="bold">
                        1
                      </Text>
                    </div>
                    <BlockStack gap="300" style={{ flex: 1 }}>
                      <BlockStack gap="200">
                        <Text variant="headingSm" as="h3">
                          {t('getStarted.quickSetup.addTrackingPage') || 'Add Tracking Page'}
                        </Text>
                        <Text color="subdued">
                          {t('getStarted.quickSetup.addTrackingPageDesc') || 'Create a dedicated tracking page in your store and add it to your navigation menu'}
                        </Text>
                      </BlockStack>
                      <Button 
                        primary
                        url="/personalization"
                      >
                        {t('getStarted.quickSetup.addTrackingPageBtn') || 'Add Tracking Page'}
                      </Button>
                    </BlockStack>
                  </InlineStack>
                </Card>

                {/* Step 2: Customize Shipping Notifications */}
                <Card padding="400">
                  <InlineStack gap="300" align="start">
                    <div style={{ 
                      backgroundColor: '#e0f5ff', 
                      padding: '12px', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '40px',
                      minHeight: '40px'
                    }}>
                      <Text variant="headingSm" as="span" fontWeight="bold">
                        2
                      </Text>
                    </div>
                    <BlockStack gap="300" style={{ flex: 1 }}>
                      <BlockStack gap="200">
                        <Text variant="headingSm" as="h3">
                          {t('getStarted.quickSetup.customizeShipping') || 'Customize Shipping Notifications'}
                        </Text>
                        <Text color="subdued">
                          {t('getStarted.quickSetup.customizeShippingDesc') || 'Configure email templates to match your brand and integrate with TrackingMania'}
                        </Text>
                      </BlockStack>
                      <Button 
                        url="/email-templates"
                      >
                        {t('getStarted.quickSetup.customizeShippingBtn') || 'Configure Templates'}
                      </Button>
                    </BlockStack>
                  </InlineStack>
                </Card>

                {/* Step 3: Email Settings */}
                <Card padding="400">
                  <InlineStack gap="300" align="start">
                    <div style={{ 
                      backgroundColor: '#fff7ed', 
                      padding: '12px', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '40px',
                      minHeight: '40px'
                    }}>
                      <Text variant="headingSm" as="span" fontWeight="bold">
                        3
                      </Text>
                    </div>
                    <BlockStack gap="300" style={{ flex: 1 }}>
                      <BlockStack gap="200">
                        <Text variant="headingSm" as="h3">
                          {t('getStarted.quickSetup.emailSettings') || 'Configure Email Settings'}
                        </Text>
                        <Text color="subdued">
                          {t('getStarted.quickSetup.emailSettingsDesc') || 'Set up your store email, sender name, and other email preferences'}
                        </Text>
                      </BlockStack>
                      <Button 
                        url="/settings#email-settings"
                      >
                        {t('getStarted.quickSetup.emailSettingsBtn') || 'Go to Email Settings'}
                      </Button>
                    </BlockStack>
                  </InlineStack>
                </Card>
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Test Email Section - Modern Design */}
        <Layout.Section>
          <Card padding="400">
            <BlockStack gap="400">
              <InlineStack gap="300" align="start">
                <div style={{ 
                  backgroundColor: '#e3f1df', 
                  padding: '12px', 
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Icon source={EmailMajor} color="success" />
                </div>
                <BlockStack gap="200">
                  <Text variant="headingMd" as="h2">
                    {t('getStarted.testEmailSection.title')}
                  </Text>
                  <Text color="subdued">
                    {t('getStarted.testEmailSection.description')}
                  </Text>
                </BlockStack>
              </InlineStack>

              <Divider />

              <InlineGrid columns={{ xs: '1', sm: '2' }} gap="400">
                <BlockStack gap="300">
                  <Select
                    label={t('getStarted.testEmailSection.selectLanguage')}
                    options={availableLanguages}
                    value={selectedLanguage}
                    onChange={setSelectedLanguage}
                    helpText={t('getStarted.testEmailSection.languageHelp')}
                  />

                  <Button
                    primary
                    size="large"
                    loading={sendingTest}
                    onClick={sendTestEmail}
                    disabled={!storeInfo?.hasEmailConfigured}
                  >
                    {t('getStarted.testEmailSection.sendTest')}
                  </Button>
                  
                  {storeInfo?.hasEmailConfigured ? (
                    <Text color="subdued">
                      {t('getStarted.testEmailSection.testWillBeSent')} <strong>{storeInfo.email}</strong>
                    </Text>
                  ) : (
                    <Text color="critical">
                      {t('getStarted.testEmailSection.configureEmail')}
                    </Text>
                  )}
                </BlockStack>
                
                <div style={{
                  backgroundColor: '#f0f9ff',
                  border: '1px solid #3b82f6',
                  borderRadius: '6px',
                  padding: '16px'
                }}>
                  <p style={{ color: '#1e40af', margin: '0', lineHeight: '1.5' }}>
                    {storeInfo?.hasEmailConfigured 
                      ? t('getStarted.testEmailSection.testInfoConfigured')
                      : t('getStarted.testEmailSection.testInfoNotConfigured')
                    }
                  </p>
                </div>
              </InlineGrid>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Next Steps - Action Cards */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <Text variant="headingMd" as="h2">
                {t('getStarted.nextSteps.title')}
              </Text>
              <Text>
                {t('getStarted.nextSteps.description')}
              </Text>
            </Box>
            <Divider />
            
            <Box padding="400">
              <InlineGrid columns={{ xs: '1', sm: '2' }} gap="400">
                {/* Configure Settings */}
                <Card padding="400">
                  <InlineStack gap="300" align="start">
                    <div style={{ 
                      backgroundColor: '#f0f9ff', 
                      padding: '12px', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Icon source={LanguageMinor} color="primary" />
                    </div>
                    <BlockStack gap="300">
                      <BlockStack gap="100">
                        <Text variant="headingSm" as="h3">
                          {t('getStarted.nextSteps.configureSettings')}
                        </Text>
                        <Text variant="bodySm" as="p" color="subdued">
                          Configure your email settings and preferences
                        </Text>
                      </BlockStack>
                      <Button url="/settings">{t('getStarted.nextSteps.configureSettingsBtn')}</Button>
                    </BlockStack>
                  </InlineStack>
                </Card>
                
                {/* Customize Templates */}
                <Card padding="400">
                  <InlineStack gap="300" align="start">
                    <div style={{ 
                      backgroundColor: '#e3f1df', 
                      padding: '12px', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Icon source={EmailMajor} color="success" />
                    </div>
                    <BlockStack gap="300">
                      <BlockStack gap="100">
                        <Text variant="headingSm" as="h3">
                          {t('getStarted.nextSteps.customizeTemplates')}
                        </Text>
                        <Text variant="bodySm" as="p" color="subdued">
                          Personalize your email templates
                        </Text>
                      </BlockStack>
                      <Button url="/personalization">{t('getStarted.nextSteps.customizeTemplatesBtn')}</Button>
                    </BlockStack>
                  </InlineStack>
                </Card>
                
                {/* Manage Journeys */}
                <Card padding="400">
                  <InlineStack gap="300" align="start">
                    <div style={{ 
                      backgroundColor: '#fff7ed', 
                      padding: '12px', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Icon source={LocationMajor} color="warning" />
                    </div>
                    <BlockStack gap="300">
                      <BlockStack gap="100">
                        <Text variant="headingSm" as="h3">
                          {t('getStarted.nextSteps.manageJourneys')}
                        </Text>
                        <Text variant="bodySm" as="p" color="subdued">
                          Monitor and manage email journeys
                        </Text>
                      </BlockStack>
                      <Button url="/email-journeys">View Journeys</Button>
                    </BlockStack>
                  </InlineStack>
                </Card>
                
                {/* Go to Dashboard */}
                <Card padding="400">
                  <InlineStack gap="300" align="start">
                    <div style={{ 
                      backgroundColor: '#e0f5ff', 
                      padding: '12px', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Icon source={MobileMajor} color="highlight" />
                    </div>
                    <BlockStack gap="300">
                      <BlockStack gap="100">
                        <Text variant="headingSm" as="h3">
                          {t('getStarted.nextSteps.monitorPerformance')}
                        </Text>
                        <Text variant="bodySm" as="p" color="subdued">
                          Track performance and analytics
                        </Text>
                      </BlockStack>
                      <Button url="/" primary>{t('common.goToDashboard')}</Button>
                    </BlockStack>
                  </InlineStack>
                </Card>
              </InlineGrid>
            </Box>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default GetStarted;